import type { PlanPriceTuples } from '@hotjar/webapp/domains/revenue/plan/types';
import type { Site } from '@hotjar/site';
import { createContext } from 'react';
import type {
  AnalyticsProduct,
  FeedbackProduct,
} from '@hotjar/webapp/domains/common/product/types';
import { noop } from '@hotjar/webapp/lib/function';
import type {
  SubmitCheckoutParams,
  SubmitCheckoutResult,
} from '@hotjar/webapp/domains/revenue/hooks/useUpsellCheckout';

import { getSalesLink } from '@hotjar/upsell';
import type {
  PlanComparisonFeatureGateDetails,
  PlanComparisonReferrer,
  TrafficRecommendationReferrer,
} from '../PlanComparisonModal/types';
import { TrafficRecommendationFeatureGate } from '../PlanComparisonModal/types';

import { getFeatureGateNameByReferrer } from '../PlanComparisonModal/utils';

export const PlanComparisonColumnsContext = createContext<{
  site: Site | null | undefined;
  recommendedPlanId: string | null | undefined;
  referrer: PlanComparisonReferrer | TrafficRecommendationReferrer | '';
  featureGate: PlanComparisonFeatureGateDetails | null | undefined;
  isUpgradeRequest: boolean;
  onCancel: (() => void) | null;
  onSubmit: ((params: SubmitCheckoutParams) => Promise<SubmitCheckoutResult>) | null;
  onError: (error: Error) => void;
}>({
  site: null,
  recommendedPlanId: null,
  referrer: '',
  featureGate: null,
  isUpgradeRequest: false,
  onCancel: null,
  onSubmit: null,
  onError: noop,
});

export const planPriceTupleIncludesPlanId = (
  planPriceTuples: PlanPriceTuples,
  candidatePlanId: string,
): boolean => planPriceTuples.map(([plan]) => plan.identifier).includes(candidatePlanId);

export const productIncludesSku = (
  products: (AnalyticsProduct | FeedbackProduct)[],
  candidatePlanId: string,
): boolean => products.some((product) => product.sku === candidatePlanId);

export const getSalesLinkByReferrer = (referrer: string): string => {
  const featureGateName = getFeatureGateNameByReferrer(referrer);

  const isTrafficFeatureGate = featureGateName === TrafficRecommendationFeatureGate.Traffic;
  const salesSource = isTrafficFeatureGate ? 'SamplingModal' : 'InAppModal';
  return getSalesLink(salesSource);
};
